import axios from "axios";
import { Provider, useDispatch, useSelector } from "react-redux";
import { store } from "./store/store";
import { useEffect, useRef, useState } from "react";
import { Outlet, useLocation, useNavigate, useParams } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import styled from "styled-components";

import FixedHeader from "./components/shopping/header/fixedHeader";
import ShoppingHeader from "./components/shopping/header/shoppingHeader";
import ShoppingFooter from "./components/shopping/footer/shoppingFooter";
import ScorllTopButton from "./components/shopping/common/scorllTopButton";
import QuickMenu from "./components/shopping/common/quickMenu";
import { handleLogin } from "./store/user";
import useUserAuthActions from "./hooks/useUserStorage.jsx";

const CustomToast = styled(ToastContainer)`
  z-index: 10000;
  width: "400px";
  font-weight: 600;
  font-size: 16px;
  @media screen and (max-width: 767px) {
    width: 100%;
  }
`;

const AppS = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { pathname } = useLocation();

  const { url } = useParams();
  const prevPath = useRef(pathname);

  const [showFixed, setShowFixed] = useState(false);
  const [cmpnyNo, setCmpnyNo] = useState("");
  const [homepageInfo, setHomepageInfo] = useState(null);
  const [isMatch, setIsMatch] = useState(false);
  const [theme, setTheme] = useState(0);
  const [menus, setMenus] = useState([]);

  const user = useSelector((state) => state?.user?.data[0]);
  const { checkAndLogin } = useUserAuthActions();

  useEffect(() => {
    if (url) {
      checkedDomain();
    } else {
      setTheme(0);
      setHomepageInfo(null);
    }
  }, [url]);

  useEffect(() => {
    if (prevPath.current !== pathname) {
      window.scrollTo({ top: 0, behavior: "auto" });
      prevPath.current = pathname;
    }
  }, [pathname]);

  useEffect(() => {
    if (!window.Kakao || !window.Kakao.isInitialized()) {
      // Kakao.init("b9886faecde9faa67a51166600a9d107"); // 개발 환경에 맞는 API 키 사용
      Kakao.init("4970bf08e349ccf4b295296035944317"); // 운영 환경에 맞는 API 키 사용
    }

    if (!user) {
      checkAndLogin();
    }

    setIsMatch(isPathMatch(pathname));
  }, [pathname, user, checkAndLogin]);

  const isPathMatch = (pathname) => {
    const basePaths = ["/shop/mypage", "/shop/login", "/shop/search"];
    const mainUrls = ["/shop/", "/shop"];
    return basePaths.some((base) => pathname.startsWith(base)) || mainUrls.includes(pathname);
  };

  // function ScrollToTop() {
  //   useEffect(() => {
  //     if (!pathname.includes("/company")) {
  //       window.scrollTo(0, 0);
  //     }

  //     changeBodyClassName();
  //   }, [pathname]);

  //   return null;
  // }

  // 회사번호 조회
  const checkedDomain = async () => {
    const requestUrl = "/api/domainSettingLoad";
    const body = {
      delYn: "N",
      domain: url,
    };

    const response = await axios.post(requestUrl, body);
    const cmpnyNo = response.data.cmpnyNo;
    if (!cmpnyNo) {
      toast.error("홈페이지가 존재하지 않습니다.", { toastId: "no-access" });
      navigate("/shop");
    } else {
      setCmpnyNo(cmpnyNo);
      await getMySetting(cmpnyNo);
      await getMenuSetting(cmpnyNo);
    }
  };

  // 홈페이지 정보 조회
  const getMySetting = async (cmpnyNo) => {
    const url = "/api/infoSettingLoad";
    const body = {
      cmpnyNo,
      delYn: "N",
    };

    const res = await axios.post(url, body);
    let data = res?.data;

    if (!data) {
      data = {};
      data.cmpnyNo = cmpnyNo;
    } else {
      document.title = data.logoText ?? data.cmpnyNm;

      if (data.logoImage) {
        updateFavicon(imgHandler(data.logoImage));
        updateMetaTag("meta[property='og:image']", imgHandler(data.logoImage));
      }

      updateMetaTag("meta[property='og:title']", data.logoText ?? data.cmpnyNm);
      updateMetaTag(
        "meta[name='description']",
        stripHtml(data.cmpnyIntroduce)
          ? stripHtml(data.cmpnyIntroduce)
          : `안녕하세요, ${data.cmpnyNm}입니다.`,
      );
      updateMetaTag(
        "meta[property='og:description']",
        data.cmpnyIntroduce ? stripHtml(data.cmpnyIntroduce) : `안녕하세요, ${data.cmpnyNm}입니다.`,
      );
      updateMetaTag("meta[property='og:url']", window.location.href);

      setHomepageInfo(data);

      const type = data?.type || 0;
      // const type = 4;
      setTheme(type);
    }
  };

  const updateFavicon = (faviconUrl) => {
    let link = document.querySelector("link[rel~='icon']");

    if (!link) {
      link = document.createElement("link");
      link.rel = "icon";
      document.head.appendChild(link);
    }

    link.href = faviconUrl;
  };

  const updateMetaTag = (selector, content) => {
    let metaTag = document.querySelector(selector);

    if (metaTag) {
      metaTag.setAttribute("content", content);
    } else {
      metaTag = document.createElement("meta");
      const property = selector.includes("og:") ? "property" : "name";
      metaTag.setAttribute(
        property,
        selector.replace("meta[property='", "").replace("meta[name='", "").replace("']", ""),
      );
      metaTag.setAttribute("content", content);
      document.head.appendChild(metaTag);
    }
  };

  const stripHtml = (htmlString) => {
    if (!htmlString) return "";
    htmlString = htmlString.replace(/<img[^>]*>/g, "");
    return htmlString.replace(/<\/?[^>]+(>|$)/g, "").trim();
  };

  // 메뉴 조회
  const getMenuSetting = async (cmpnyNo) => {
    const url = "/api/menuSetting";
    const body = { cmpnyNo: cmpnyNo };
    const res = await axios.post(url, body);

    if (res.status === 200 && res.data.length > 0) {
      const data = res.data
        .filter((item) => item.delYn === "N" && item.displayYn === "Y")
        .sort((a, b) => {
          if (a.seq > b.seq) {
            return 1;
          }
          if (a.seq < b.seq) {
            return -1;
          }
          return 0;
        });

      setMenus(data);
    } else {
      const data = createDefaultMenu();
      setMenus(data);
    }
  };

  const createDefaultMenu = (cmpnyNo) => {
    const names = ["회사소개", "제품소개", "포트폴리오", "공지사항"];
    const data = [];

    for (let i = 0; i < names.length; i++) {
      data.push({
        menuType: `${i}`,
        displayYn: "N",
        beforeMenuName: names[i],
        afterMenuName: "",
        seq: i + 1,
        cmpnyNo: cmpnyNo,
        delYn: "N",
      });
    }

    return data;
  };

  useEffect(() => {
    const handleScroll = () => {
      setShowFixed(window.scrollY > 195);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <Provider store={store}>
      {/* <ScrollToTop /> */}
      <CustomToast
        position="top-center"
        theme="light"
        pauseOnHover
        hideProgressBar={true}
        autoClose={1000}
        closeButton={false}
      />
      <div className="sub_wrap">
        <ShoppingHeader
          key={`Header-${url}-${cmpnyNo}-${theme}`}
          url={url}
          homepageInfo={homepageInfo}
          theme={theme}
          isMatch={isMatch}
          menus={menus}
        />
        {showFixed && ["0", "2"].includes(String(theme)) && !isMatch && (
          <FixedHeader
            homepageInfo={homepageInfo}
            theme={theme}
            isMatch={isMatch}
            menus={menus}
            url={url}
          />
        )}
        <Outlet
          context={{ cmpnyNo, url, homepageInfo, theme, menus, isMatch }}
          key={`Outlet-${url}-${cmpnyNo}-${theme}`}
        />
        <QuickMenu theme={theme} cmpnyNo={cmpnyNo} homepageInfo={homepageInfo} />
        <ScorllTopButton theme={theme} />
        {url && (
          <ShoppingFooter
            key={`ShoppingFooter-${url}-${cmpnyNo}-${theme}`}
            homepageInfo={homepageInfo}
            theme={theme}
            isMatch={isMatch}
          />
        )}
      </div>
    </Provider>
  );
};

export default AppS;
